import {
  DurationPickerV2 as DurationPicker,
  FieldError,
  NumberField,
  TextField,
  castTimeBreakdownStrings,
  getTimeBreakdownStrings,
  getTotalSeconds,
} from '@automata/ui';
import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';

import {
  Errors,
  NumberParameterDefinition,
  ParameterType,
} from '../GraphEditorToolbars/ParametersToolbar/types';
import {
  getParameterType,
  getParameterValidationErrors,
  getParameterValidationSchema,
} from '../GraphEditorToolbars/ParametersToolbar/utils';

interface AccordionParameterReferenceProps {
  isUserHaveCreatorAccess?: boolean;
  parameter: ParameterDefinition;
  changeParameter: (parameter: ParameterDefinition, isValid: boolean) => void;
}

export const AccordionParameterReference = ({
  isUserHaveCreatorAccess = true,
  parameter,
  changeParameter,
}: AccordionParameterReferenceProps) => {
  const [errors, setErrors] = useState<Errors>({});
  const type = getParameterType(parameter);

  const handleFieldChange = useCallback(
    <K extends keyof ParameterDefinition>(
      field: K,
      value: ParameterDefinition[K]
    ) => {
      const schema = getParameterValidationSchema(type);
      const newParameter = {
        ...parameter,
        [field]: value,
      };

      const parsed = schema.safeParse(newParameter);
      const errors = getParameterValidationErrors(parsed);

      setErrors(errors);
      changeParameter(newParameter, parsed.success);
    },
    [parameter, type, changeParameter]
  );

  let field: ReactNode;

  switch (type) {
    case ParameterType.STRING:
      field = (
        <TextField
          fullWidth
          disabled={!isUserHaveCreatorAccess}
          error={!!errors.default}
          label={`${parameter.name} - ${type}`}
          value={typeof parameter.default === 'string' ? parameter.default : ''}
          onChange={(e) => handleFieldChange('default', e.target.value)}
        />
      );
      break;
    case ParameterType.INTEGER:
    case ParameterType.FLOAT:
      field = (
        <NumberField
          decimal={type === ParameterType.FLOAT}
          disabled={!isUserHaveCreatorAccess}
          error={!!errors.default}
          fullWidth
          label={`${parameter.name} - ${type}`}
          max={(parameter as NumberParameterDefinition).maximum ?? undefined}
          min={(parameter as NumberParameterDefinition).minimum ?? undefined}
          value={
            typeof parameter.default === 'number' ? parameter.default : null
          }
          onChange={(e) => {
            if (e !== null) {
              handleFieldChange('default', e);
            }
          }}
        />
      );
      break;
    case ParameterType.BOOLEAN:
      field = (
        <Stack paddingTop={1}>
          <Typography lineHeight={2} variant="overline">
            {parameter.name} - {type}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={
                  typeof parameter.default === 'boolean'
                    ? parameter.default
                    : false
                }
                id="canvas-parameterReference-switch"
                onChange={() =>
                  handleFieldChange('default', !parameter.default)
                }
              />
            }
            label={parameter.default ? 'True' : 'False'}
          />
        </Stack>
      );
      break;
    case ParameterType.DURATION: {
      const { hours, minutes, seconds } = getTimeBreakdownStrings(
        typeof parameter.default === 'number' ? parameter.default : undefined
      );

      field = (
        <DurationPicker
          disabled={!isUserHaveCreatorAccess}
          error={!!errors.default}
          hours={hours}
          label={`${parameter.name} - ${type}`}
          minutes={minutes}
          seconds={seconds}
          handleUpdateTime={(timePatch) => {
            const totalSeconds = getTotalSeconds(
              castTimeBreakdownStrings({
                hours,
                minutes,
                seconds,
                ...timePatch,
              })
            );
            handleFieldChange(
              'default',
              totalSeconds >= 0 ? totalSeconds : null
            );
          }}
        />
      );
    }
  }

  return (
    <Box>
      {field}
      <FieldError error={errors.default?.message} />
    </Box>
  );
};
